import { useState } from "react";
import { Gift, CheckCircle2, Clock, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { RedeemDialog } from "./RedeemDialog";
import { LoadedBundles } from "./LoadedBundles";

const gb = (mb) => (mb >= 1000 ? `${(mb / 1000).toFixed(mb % 1000 ? 1 : 0)}GB` : `${mb}MB`);

export const RewardsCard = ({ phone, wallet, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(null);
  if (!wallet) return null;
  const max = wallet.tiers[wallet.tiers.length - 1];
  const pct = Math.min((wallet.balance_mb / max) * 100, 100);
  const pending = wallet.redemptions.filter((r) => r.status === "pending");
  const lastNumber = wallet.redemptions.find((r) => r.megatel_number)?.megatel_number || "";

  const redeem = async (num) => {
    setBusy(true);
    try {
      onChange(await api.redeem(phone, picked, num));
      toast.success(`${gb(picked)} redemption requested. Your data will be loaded to ${num} on Megatel.`);
      setPicked(null);
    } catch { toast.error("Could not redeem"); } finally { setBusy(false); }
  };

  return (
    <section className="rewards-card relative mx-5 mt-6 rounded-3xl p-[1.5px]" data-testid="rewards-card">
      <div className="relative rounded-[22px] bg-[#0d1a14] p-5 overflow-hidden">
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#32D74B]/25 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-10 w-40 h-40 rounded-full bg-[#00C6FF]/15 blur-3xl pointer-events-none" />
      <div className="relative flex items-start justify-between">
        <div>
          <img src="/megatel-wordmark.png" alt="Megatel" className="h-6 w-auto" data-testid="rewards-megatel-logo" />
          <div className="mt-1.5 flex items-center gap-1.5 text-xs text-[#7CF29A] font-medium"><Gift className="w-3.5 h-3.5" /> Data rewards</div>
        </div>
        <div className="text-right">
          <div className="font-rounded font-bold text-4xl tabular-nums text-white drop-shadow-[0_0_12px_rgba(50,215,75,0.5)]" data-testid="rewards-balance">{gb(wallet.balance_mb)}</div>
          <div className="text-[10px] text-neutral-400 uppercase tracking-wider">available</div>
        </div>
      </div>

      <div className="relative mt-5 h-3 rounded-full bg-white/[0.08]" data-testid="rewards-progress">
        <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#32D74B] to-[#00C6FF] transition-[width] duration-500" style={{ width: `${pct}%` }} />
        {wallet.tiers.map((t) => (
          <span key={t} className="absolute -top-1 w-0.5 h-4.5 bg-white/30" style={{ left: `${(t / max) * 100}%`, height: 18 }} />
        ))}
      </div>
      <div className="relative h-4 mt-1 text-[10px] text-neutral-500">
        {wallet.tiers.map((t) => <span key={t} className="absolute -translate-x-full" style={{ left: `${(t / max) * 100}%` }}>{gb(t)}</span>)}
      </div>

      <div className="mt-2 text-xs flex items-center gap-1.5" data-testid="rewards-week-status">
        {wallet.earned_this_week
          ? <><CheckCircle2 className="w-3.5 h-3.5 text-[#32D74B]" /><span className="text-neutral-300">This week's {wallet.reward_mb}MB earned. Next reward from Mon {wallet.next_eligible}</span></>
          : <><Gift className="w-3.5 h-3.5 text-[#32D74B]" /><span className="text-neutral-300">Run a scan with location on to earn {wallet.reward_mb}MB this week</span></>}
      </div>

      <div className="grid grid-cols-2 gap-2 mt-4">
        {wallet.tiers.map((t) => (
          <button key={t} data-testid={`redeem-${t}-button`} disabled={busy || wallet.balance_mb < t} onClick={() => setPicked(t)}
            className="rounded-full py-2.5 text-sm font-medium bg-[#32D74B] text-black disabled:bg-white/[0.06] disabled:text-neutral-500 transition-colors">
            Redeem {gb(t)}
          </button>
        ))}
      </div>

      {pending.length > 0 && (
        <div className="mt-3 space-y-1" data-testid="rewards-pending">
          {pending.map((r) => (
            <div key={r.id} className="flex items-center gap-1.5 text-[11px] text-[#FF9F0A]"><Clock className="w-3 h-3" />{gb(r.mb)} pending to {r.megatel_number || phone}: requested {new Date(r.requested_at).toLocaleDateString()}</div>
          ))}
        </div>
      )}
      <RedeemDialog key={picked} termsUrl={wallet.terms_url} validityDays={wallet.validity_days} mb={picked} initial={lastNumber} label={picked ? gb(picked) : ""} busy={busy} onClose={() => setPicked(null)} onConfirm={redeem} />
      <LoadedBundles redemptions={wallet.redemptions} />
      {wallet.terms_url && (
        <a href={wallet.terms_url} target="_blank" rel="noopener noreferrer" data-testid="rewards-terms-link"
          className="mt-3 inline-flex items-center gap-1 text-[11px] text-[#7CF29A] underline underline-offset-2 hover:text-white">
          Rewards terms & conditions <ExternalLink className="w-3 h-3" />
        </a>
      )}
      <div className="mt-2 text-[10px] text-neutral-600">Earned {gb(wallet.earned_mb)} · Redeemed {gb(wallet.redeemed_mb)} · 1 reward per week (Mon–Sun)</div>
      </div>
    </section>
  );
};
